// THE CAPABILITY RESOLVER — fail-closed, keyed on `roles[]`.
//
// ---------------------------------------------------------------------------
// THE BUG THIS EXISTS TO MAKE IMPOSSIBLE
// ---------------------------------------------------------------------------
// Document-Studio shipped a viewer that could EDIT: an unrecognised role fell through to
// a WRITABLE default. The failure is not that the gate was missing — it is that the
// DEFAULT branch was the permissive one, so every case the author did not enumerate
// became a write grant, silently, and only for the users nobody tested.
//
// So the shape here is deliberate and the ordering is the control:
//
//   * the resolver's LAST statement — the fall-through — returns `VIEW_ONLY`, the
//     least-privileged tier, with every write capability `false`;
//   * writes are never asked as "is this user elevated?" but as "does this user hold
//     THIS capability?", through one chokepoint (`can`), so a new write surface cannot
//     be added without naming the capability it needs;
//   * the tier table is frozen, so a caller cannot flip a flag on the shared object and
//     widen every session at once.
//
// ---------------------------------------------------------------------------
// THE ROLE KEYS, AND WHY THEY ARE SPELLED INCONSISTENTLY
// ---------------------------------------------------------------------------
// They mirror a real, unresolved inconsistency rather than papering over it.
//
// BOTH keys are HYPHEN, and both are the AUTHORITATIVE PROD strings on connect.anshin.us,
// confirmed verbatim by BeeLink-Connect 2026-10-24: ELEVATED = `anna-demo`, READ-ONLY =
// `anna-viewer`. The public demo (anna-sdk.anshin.us) authenticates against PROD, so PROD
// is the grant this resolver must match.
//
// CORRECTED 2026-10-24. An earlier DEV snapshot (2026-10-19, see live-shape.test.ts) keyed
// ELEVATED on `anna_demo`, UNDERSCORE, because admin@ then appeared to hold only that. That
// snapshot predated the 2026-10-20 three-tier + viewer-fix provisioning, which grants
// `anna-demo` (hyphen) as elevated and `anna-viewer` to viewer@. On prod, admin@ currently
// carries BOTH `anna-demo` (current) and `anna_demo` (stale cruft from the old
// lowercase-underscore run) — which is exactly why keying on the underscore LOOKED like it
// worked: admin happened to still hold the cruft. Connect is cleaning that grant up and
// treats the underscore as non-existent; an underscore-keyed resolver then falls admin
// through to view-only and closes the elevated gate for everyone — the "looks like a working
// gate" failure this file exists to make impossible. The HYPHEN survives the cleanup.
// `hasRole` matches exactly, so the spelling must be verbatim.
//
// Both are single constants: a correction from Connect is one line each.

import { hasRole, type ResolvedSession } from './dto';

/** Grants every demo capability, including writes. PROD key, confirmed by Connect 2026-10-24. */
export const ELEVATED_ROLE = 'anna-demo';

/** Grants viewing and launching, no writes. PROD key; granted to viewer@ post-2026-10-20. */
export const READ_ONLY_ROLE = 'anna-viewer';

export type CapabilityTier = 'elevated' | 'read-only' | 'view-only';

/**
 * What a session may do.
 *
 * Every field is a REQUIRED boolean. No optionals: an absent capability would read as
 * `undefined`, which is falsy today and becomes a silent grant the moment anyone writes
 * `caps.writeSettings !== false`.
 */
export interface Capabilities {
  readonly tier: CapabilityTier;
  /** Read the demo's pages. True in every tier — there is no "no access" tier here. */
  readonly viewPages: boolean;
  /** Mount and click the Anna launcher. */
  readonly launchAnna: boolean;
  /** Change the demo's settings (target env, caller src, context summary). WRITE. */
  readonly writeSettings: boolean;
  /** Reset the demo sandbox to its initial state. WRITE. */
  readonly resetSandbox: boolean;
  /** Switch back out of an emulated persona. WRITE. */
  readonly switchPersona: boolean;
}

/**
 * The capability names that constitute a WRITE.
 *
 * Enumerated as data, not implied by a naming convention, so the invariant test can
 * assert over all of them and a new write capability must be added here to be counted.
 * `viewPages` and `launchAnna` are deliberately absent — launching Anna opens a window,
 * it mutates nothing in this demo.
 */
export const WRITE_CAPABILITIES = ['writeSettings', 'resetSandbox', 'switchPersona'] as const;
export type WriteCapability = (typeof WRITE_CAPABILITIES)[number];

/** Any capability a caller may ask about. */
export type CapabilityName = Exclude<keyof Capabilities, 'tier'>;

/**
 * LEAST PRIVILEGE. The fall-through tier, and the value returned for every role the
 * resolver does not recognise.
 *
 * Declared FIRST in this file on purpose: it is the default, and a reader should meet the
 * default before the grants.
 */
export const VIEW_ONLY: Capabilities = Object.freeze({
  tier: 'view-only',
  viewPages: true,
  launchAnna: false,
  writeSettings: false,
  resetSandbox: false,
  switchPersona: false,
});

/** View everything and launch Anna. No writes. */
export const READ_ONLY: Capabilities = Object.freeze({
  tier: 'read-only',
  viewPages: true,
  launchAnna: true,
  writeSettings: false,
  resetSandbox: false,
  switchPersona: false,
});

/** Everything. */
export const ELEVATED: Capabilities = Object.freeze({
  tier: 'elevated',
  viewPages: true,
  launchAnna: true,
  writeSettings: true,
  resetSandbox: true,
  switchPersona: true,
});

/**
 * Resolve a session's capabilities from its roles.
 *
 * THE ORDER IS THE CONTROL. Elevated is checked first, then read-only, and anything that
 * reaches the end — an unrecognised role, a non-elevated role, zero roles, a role from
 * another SDK — gets `VIEW_ONLY`. The permissive tiers are reachable only by an EXACT
 * role match; the restrictive tier is the one you fall into.
 *
 * There is no author/propose tier. The launcher has no four-eyes workflow, so inventing
 * one would be a state nothing can produce and nothing consumes.
 */
export function resolveCapabilities(session: ResolvedSession): Capabilities {
  if (hasRole(session, ELEVATED_ROLE)) return ELEVATED;
  if (hasRole(session, READ_ONLY_ROLE)) return READ_ONLY;
  return VIEW_ONLY;
}

/**
 * THE WRITE CHOKEPOINT. Does this session hold this specific capability?
 *
 * Every gated surface asks through here, naming the capability it needs. Asking
 * "is the tier elevated?" at a call site would work today and would be the thing that
 * rots: a tier added later, or a capability moved between tiers, changes the answer in
 * one place here instead of in every call site that happened to compare a string.
 *
 * Returns FALSE for anything it cannot positively confirm — including a `caps` object
 * that is missing the field, which `Boolean(...) === true` makes explicit rather than
 * relying on the field being present.
 */
export function can(caps: Capabilities, capability: CapabilityName): boolean {
  return caps[capability] === true;
}

/** Convenience for the common question, routed through the same chokepoint. */
export function canWrite(caps: Capabilities, capability: WriteCapability): boolean {
  return can(caps, capability);
}

/**
 * Is this tier allowed to write anything at all?
 *
 * Computed from `WRITE_CAPABILITIES` rather than from `tier === 'elevated'`, so it stays
 * true to the table if the table changes. Used by the capability matrix in the UI and by
 * the invariant test.
 */
export function hasAnyWrite(caps: Capabilities): boolean {
  return WRITE_CAPABILITIES.some((c) => can(caps, c));
}

/** The three tiers, for the security-tour matrix. Ordered least-privileged first. */
export const ALL_TIERS: readonly Capabilities[] = Object.freeze([VIEW_ONLY, READ_ONLY, ELEVATED]);

/** Human-readable reason a tier was assigned, for the UI. */
export function describeTier(caps: Capabilities): string {
  switch (caps.tier) {
    case 'elevated':
      return `holds ${ELEVATED_ROLE} — all demo features, including writes`;
    case 'read-only':
      return `holds ${READ_ONLY_ROLE} — may view and launch, may not write`;
    case 'view-only':
      return `holds neither ${ELEVATED_ROLE} nor ${READ_ONLY_ROLE} — least privilege, view only`;
  }
}
